import { isTen, suitOf, type CardId } from './cards';
import { explainIllegal, legalActions } from './legal';
import { nextDealer, scoreHand } from './outcome';
import { toSeatView } from './projection';
import { buildDeck, DECK_RECIPES } from './recipes';
import { shuffle, type Rng } from './rng';
import { rulesStats, validateRules, type RulesConfig } from './rules';
import { resolveTrick } from './trick';
import { initialTrump, needsReserve, revealAtTrumpRequest, revealAtTurnStart } from './trump';
import type {
  Action,
  ApplyResult,
  GameEvent,
  HandState,
  MatchState,
  Seat,
  TaggedEvent,
} from './types';
import { teamOf } from './types';

class EventSink {
  readonly events: TaggedEvent[] = [];
  pub(e: GameEvent): void {
    this.events.push({ to: 'public', e });
  }
  priv(seat: Seat, e: GameEvent): void {
    this.events.push({ to: seat, e });
  }
}

/** JSON-safe deep clone: state is plain data, and this works on every JS runtime incl. Hermes. */
function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export interface CreateMatchOptions {
  rules: RulesConfig;
  matchId: string;
  rng: Rng;
  /** Defaults to a random seat (PRD §4.1: first dealer chosen randomly). */
  firstDealer?: Seat;
}

export function createMatch(opts: CreateMatchOptions): {
  state: MatchState;
  events: TaggedEvent[];
} {
  const errors = validateRules(opts.rules);
  if (errors.length > 0) throw new Error(`Invalid rules: ${errors.join('; ')}`);
  const players = opts.rules.playerCount;
  const dealer = opts.firstDealer ?? opts.rng.int(players);
  const state: MatchState = {
    rules: { ...opts.rules },
    matchId: opts.matchId,
    phase: 'IN_HAND',
    dealer,
    score: [0, 0],
    handsPlayed: 0,
    // Replaced by startHand below.
    hand: undefined as unknown as HandState,
    results: [],
    winner: null,
  };
  const sink = new EventSink();
  startHand(state, opts.rng, sink);
  return { state, events: sink.events };
}

export function apply(
  state: MatchState,
  action: Action,
  actor: Seat | 'system',
  rng: Rng,
): ApplyResult {
  if (state.phase === 'MATCH_OVER') return { ok: false, code: 'MATCH_OVER' };

  if (action.type === 'NEXT_HAND') {
    if (state.phase !== 'HAND_OVER') return { ok: false, code: 'WRONG_PHASE' };
    const next = clone(state);
    const sink = new EventSink();
    startHand(next, rng, sink);
    return { ok: true, state: next, events: sink.events };
  }

  if (actor === 'system') return { ok: false, code: 'ILLEGAL_ACTION' };
  if (state.phase !== 'IN_HAND') return { ok: false, code: 'WRONG_PHASE' };
  if (actor < 0 || actor >= state.rules.playerCount) return { ok: false, code: 'NOT_IN_HAND' };

  const view = toSeatView(state, actor);
  const legal = legalActions(view);
  const allowed = legal.some(
    (a) =>
      a.type === action.type &&
      (!('cardId' in a) || ('cardId' in action && a.cardId === action.cardId)),
  );
  if (!allowed) return { ok: false, code: rejectReason(state, actor, action) };

  const next = clone(state);
  const sink = new EventSink();
  switch (action.type) {
    case 'RESERVE_TRUMP':
      reserve(next, actor, action.cardId, sink);
      break;
    case 'PLAY_CARD':
      play(next, actor, action.cardId, sink);
      break;
    case 'REQUEST_REVEAL':
      sink.pub(revealAtTrumpRequest(next.hand, actor));
      break;
  }
  return { ok: true, state: next, events: sink.events };
}

function rejectReason(state: MatchState, actor: Seat, action: Action) {
  const view = toSeatView(state, actor);
  const hand = state.hand;
  switch (action.type) {
    case 'RESERVE_TRUMP':
      if (hand.phase !== 'RESERVE') return 'WRONG_PHASE' as const;
      if (hand.trump.reserverSeat !== actor) return 'NOT_RESERVER' as const;
      return explainIllegal(view, action.cardId) ?? ('ILLEGAL_ACTION' as const);
    case 'PLAY_CARD':
      if (hand.phase !== 'PLAY') return 'WRONG_PHASE' as const;
      if (hand.active !== actor) return 'NOT_YOUR_TURN' as const;
      return explainIllegal(view, action.cardId) ?? ('ILLEGAL_ACTION' as const);
    case 'REQUEST_REVEAL':
      if (hand.active !== actor) return 'NOT_YOUR_TURN' as const;
      return 'REVEAL_NOT_ALLOWED' as const;
    default:
      return 'ILLEGAL_ACTION' as const;
  }
}

function startHand(state: MatchState, rng: Rng, sink: EventSink): void {
  const { rules } = state;
  const players = rules.playerCount;
  const recipe = DECK_RECIPES[rules.deckRecipe];
  const stats = rulesStats(rules);
  const handNumber = state.handsPlayed + 1;
  const dealer = state.dealer;
  const opener = (dealer + 1) % players;
  const trump = initialTrump(rules.trumpMode, dealer, players, rng);

  const hand: HandState = {
    handNumber,
    handId: `${state.matchId}.h${handNumber}`,
    dealer,
    phase: 'PLAY',
    hands: Array.from({ length: players }, () => []),
    undealt: shuffle(buildDeck(recipe), rng),
    reserved: null,
    trump,
    leader: opener,
    active: opener,
    trickIndex: 0,
    trick: [],
    captured: [[], []],
    tricksWon: [0, 0],
    tricks: [],
  };
  state.hand = hand;
  state.phase = 'IN_HAND';

  sink.pub({
    t: 'HAND_STARTED',
    handNumber,
    handId: hand.handId,
    dealer,
    trumpMode: rules.trumpMode,
    openTrumpSuit: trump.status === 'OPEN' ? trump.suit : null,
    handSize: stats.handSize,
  });

  if (needsReserve(rules.trumpMode)) {
    dealPacket(hand, recipe.packets, 0, players, sink);
    hand.phase = 'RESERVE';
    hand.active = trump.reserverSeat!;
    sink.pub({ t: 'RESERVE_REQUIRED', seat: trump.reserverSeat! });
    return;
  }
  for (let p = 0; p < recipe.packets.length; p++)
    dealPacket(hand, recipe.packets, p, players, sink);
  beginTurn(hand, opener, sink);
}

/** Deal packet `index` to every seat, starting at the dealer's right (PRD §4.1). */
function dealPacket(
  hand: HandState,
  packets: readonly number[],
  index: number,
  players: number,
  sink: EventSink,
): void {
  const size = packets[index]!;
  for (let k = 0; k < players; k++) {
    const seat = (hand.dealer + 1 + k) % players;
    const cards = hand.undealt.splice(0, size);
    hand.hands[seat]!.push(...cards);
  }
  sink.pub({
    t: 'CARDS_DEALT',
    packet: index,
    size,
    // Public counts include a face-down Band reserve, matching toPublic().handCounts.
    counts: hand.hands.map(
      (h, seat) => h.length + (hand.reserved !== null && seat === hand.trump.reserverSeat ? 1 : 0),
    ),
  });
  for (let seat = 0; seat < players; seat++) {
    sink.priv(seat, {
      t: 'HAND_RECEIVED',
      packet: index,
      cards: hand.hands[seat]!.slice(hand.hands[seat]!.length - size),
    });
  }
}

function reserve(state: MatchState, seat: Seat, cardId: CardId, sink: EventSink): void {
  const hand = state.hand;
  const recipe = DECK_RECIPES[state.rules.deckRecipe];
  removeCard(hand.hands[seat]!, cardId);
  hand.reserved = cardId;
  sink.pub({ t: 'TRUMP_RESERVED', seat });
  sink.priv(seat, { t: 'RESERVED_CARD', cardId });
  for (let p = 1; p < recipe.packets.length; p++) {
    dealPacket(hand, recipe.packets, p, state.rules.playerCount, sink);
  }
  hand.phase = 'PLAY';
  beginTurn(hand, hand.leader, sink);
}

function play(state: MatchState, seat: Seat, cardId: CardId, sink: EventSink): void {
  const hand = state.hand;
  const players = state.rules.playerCount;
  const order = hand.trick.length;

  if (hand.trump.mode === 'CUT' && hand.trump.status === 'NONE' && order > 0) {
    const led = suitOf(hand.trick[0]!.cardId);
    const suit = suitOf(cardId);
    if (suit !== led) {
      hand.trump = {
        ...hand.trump,
        status: 'SET',
        suit,
        cardId,
        activatedAt: { trick: hand.trickIndex, order },
      };
      sink.pub({ t: 'TRUMP_SET', seat, suit, cardId });
    }
  }

  removeCard(hand.hands[seat]!, cardId);
  hand.trick.push({ seat, cardId, order });
  if (hand.trump.requestedBy === seat) hand.trump = { ...hand.trump, requestedBy: null };
  sink.pub({ t: 'CARD_PLAYED', seat, cardId, trickIndex: hand.trickIndex, order });

  if (hand.trick.length < players) {
    beginTurn(hand, (seat + 1) % players, sink);
    return;
  }
  completeTrick(state, sink);
}

function completeTrick(state: MatchState, sink: EventSink): void {
  const hand = state.hand;
  const winner = resolveTrick(hand.trick, hand.trickIndex, hand.trump, state.rules.duplicateTie);
  const team = teamOf(winner.seat);
  const cards = hand.trick.map((p) => p.cardId);
  const tens = cards.filter(isTen).length;
  hand.captured[team].push(...cards);
  hand.tricksWon[team] += 1;
  hand.tricks.push({ trickIndex: hand.trickIndex, plays: hand.trick, winner: winner.seat, tens });
  sink.pub({ t: 'TRICK_WON', trickIndex: hand.trickIndex, winner: winner.seat, team, cards, tens });

  hand.trick = [];
  hand.trickIndex += 1;
  const stats = rulesStats(state.rules);
  if (hand.trickIndex >= stats.tricks) {
    finishHand(state, sink);
    return;
  }
  hand.leader = winner.seat;
  beginTurn(hand, winner.seat, sink);
}

function finishHand(state: MatchState, sink: EventSink): void {
  const { rules, hand } = state;
  const stats = rulesStats(rules);
  const partial = scoreHand(hand, rules, stats.tens, stats.tricks);
  const result = {
    ...partial,
    nextDealer: nextDealer(rules.dealerPolicy, hand.dealer, rules.playerCount, partial),
  };
  hand.phase = 'DONE';
  state.results.push(result);
  state.handsPlayed += 1;
  state.dealer = result.nextDealer;
  if (result.winnerTeam !== null) state.score[result.winnerTeam] += result.points;
  sink.pub({ t: 'HAND_SCORED', result, score: [state.score[0], state.score[1]] });

  if (result.winnerTeam !== null && state.score[result.winnerTeam] >= rules.target) {
    state.phase = 'MATCH_OVER';
    state.winner = result.winnerTeam;
    sink.pub({
      t: 'MATCH_COMPLETE',
      winner: result.winnerTeam,
      score: [state.score[0], state.score[1]],
    });
    return;
  }
  state.phase = 'HAND_OVER';
}

function beginTurn(hand: HandState, seat: Seat, sink: EventSink): void {
  hand.active = seat;
  const reveal = revealAtTurnStart(hand, seat);
  if (reveal) sink.pub(reveal);
  sink.pub({ t: 'TURN_STARTED', seat, trickIndex: hand.trickIndex });
}

function removeCard(cards: CardId[], cardId: CardId): void {
  const index = cards.indexOf(cardId);
  if (index < 0) throw new Error(`Card ${cardId} not in hand`);
  cards.splice(index, 1);
}
